import type { FileWithId } from 'src/types';

import { PipelineName } from './useCreateRun';

const llm_response_storage_path = '${global_config.defaults.storage_base}';
interface ModelConfig {
  model_name: string;
  max_output_token_length: number;
  temperature: number;
  top_p: number;
  top_k: number;
  knowledge_base?: string[];
  knowledge_base_type?: string;
}

interface Message {
  role: string;
  content: {
    type: string;
    [key: string]: string | string[] | undefined;
  }[];
}

const STORAGE_BASE = `s3://podium-ia-call-summarization-app-documents-${process.env.ENTERPRISE_NAME}-${process.env.NODE_ENV}`;

// Default prompt message used to improve performance
const prompMessage = {
  role: 'user',
  content: [
    {
      type: 'text',
      text:
        'You are a helpful AI assistant at Xpanse. You respond in brief, unless asked otherwise.',
    },
  ],
};

export const formatConfigJSON = (
  mode: string,
  messages?: Message[],
  modelConfig?: ModelConfig,
  fileName?: string,
  audioFile?: FileWithId
) => {
  switch (mode) {
    case 'chat':
      return {
        version: '1.0',
        description: 'Config JSON for development',
        author: 'Gen AI Team',
        created: new Date().toISOString(),
        global_config: {
          secret_manager_name: 'podium-ia-call-summarization-app-secrets',
          defaults: {
            region: 'us-east-1',
            language_model: 'anthropic.claude-3-5-sonnet-20240620-v1:0',
            storage_base: STORAGE_BASE,
            is_local_testing: 'False',
            max_output_token_length: 4096,
            temperature: 0.1,
            top_p: 0.9,
            top_k: 40,
          },
        },
        pipelines: [
          {
            name: PipelineName.CHATBOT,
            description:
              'Pipeline which takes documents and conversation history to get response from llm',
            enabled: 'True',
            inputs: {
              call_input: {
                type: 'record_id',
                value: '',
              },
            },
            modules: ['chatbot_module'],
          },
        ],
        modules: [
          {
            name: 'chatbot_module',
            outputs: {
              llm_response: `${llm_response_storage_path}/runs/${PipelineName.CHATBOT}.inputs.run_id/llm_response.txt`,
            },
            type: 'llm_chat_completion_module',
            description: 'LLM module for testing',
            llm_config: {
              provider_config: {
                model_name: modelConfig?.model_name,
              },
              max_output_token_length: modelConfig?.max_output_token_length,
              temperature: modelConfig?.temperature,
              top_p: modelConfig?.top_p,
              top_k: modelConfig?.top_k,
            },
            messages: [prompMessage, ...messages || []],
          },
        ],
      };
    case 'chat_with_kb':
      return {
        version: '1.0',
        description: 'Chatbot with knowledgebase',
        author: 'Gen AI Team',
        created: new Date().toISOString(),

        global_config: {
          secret_manager_name: 'podium-ia-call-summarization-app-secrets',
          defaults: {
            region: 'us-east-1',
            max_output_token_length: 2048,
            temperature: 0.1,
            top_p: 0.9,
            top_k: 40,
            knowledge_base_type: 'postgres',
            similarity_threshold: 0.3,
            limit: 5,
            language_model: 'anthropic.claude-3-5-sonnet-20240620-v1:0',
            storage_base: STORAGE_BASE,
            is_local_testing: 'False',
          },
        },
        pipelines: [
          {
            name: PipelineName.CHAT_WITH_KNOWLEDGE_BASE,
            description: 'Pipeline to chat with knowledge base',
            enabled: 'True',
            inputs: {
              call_input: {
                type: 'record_id',
                value: '',
              },
              llm_config: {
                provider_config: {
                  model_name: modelConfig?.model_name,
                },
                max_output_token_length: modelConfig?.max_output_token_length,
                temperature: modelConfig?.temperature,
                top_p: modelConfig?.top_p,
                top_k: modelConfig?.top_k,
              },
              knowledge_base_type: {
                type: 'string',
                value: modelConfig?.knowledge_base_type,
              },
              knowledge_base_id: {
                type: 'array',
                value: modelConfig && modelConfig.knowledge_base,
              },
              similarity_threshold: {
                type: 'float',
                value: 0.5,
              },
              limit: {
                type: 'int',
                value: 5,
              },
              messages,
              run_id: 'llm_test',
            },
            modules: [
              'standalone_query_module',
              'knowledge_base_retrieval_module',
              'chatbot_module',
            ],
          },
        ],
        modules: [
          {
            name: 'standalone_query_module',
            type: 'llm_chat_completion_module',
            description: 'Make a standalone query to knowledge base',
            llm_config: '${Chat_with_Knowledge_Base.inputs.llm_config}',
            messages: '${Chat_with_Knowledge_Base.inputs.messages}',
            inputs: {},
            outputs: {
              llm_response:
                '${global_config.defaults.storage_base}/${Chat_with_Knowledge_Base.inputs.run_id}/standalone_query.txt',
            },
          },
          {
            name: 'knowledge_base_retrieval_module',
            model: 'knowledge base retriever',
            type: 'knowledge_base_retriever_module',
            description: 'Knowledge base retrieval module',
            inputs: {
              query: {
                type: 'text',
                value: '${standalone_query_module.outputs.llm_response}',
              },
              knowledge_base_type: '${Chat_with_Knowledge_Base.inputs.knowledge_base_type}',
              knowledge_base_id: '${Chat_with_Knowledge_Base.inputs.knowledge_base_id}',
              similarity_threshold: '${Chat_with_Knowledge_Base.inputs.similarity_threshold}',
              limit: '${Chat_with_Knowledge_Base.inputs.limit}',
            },
            outputs: {
              relevant_chunks:
                '${global_config.defaults.storage_base}/${Chat_with_Knowledge_Base.inputs.run_id}/relevant_chunks.json',
            },
            depends_on: ['standalone_query_module'],
          },
          {
            name: 'chatbot_module',
            type: 'llm_chat_completion_module',
            description: 'Responds to the user query with relevant chunks from knowledge base',
            llm_config: '${Chat_with_Knowledge_Base.inputs.llm_config}',
            messages: '${Chat_with_Knowledge_Base.inputs.messages}',
            inputs: {
              knowledge_base: '${knowledge_base_retrieval_module.outputs.relevant_chunks}',
            },
            outputs: {
              llm_response:
                '${global_config.defaults.storage_base}/${Chat_with_Knowledge_Base.inputs.run_id}/final_response.txt',
            },
            depends_on: ['knowledge_base_retrieval_module'],
          },
        ],
      };
    case 'chat_with_audio':
      return {
        version: '1.0',
        description: 'Config JSON for development',
        author: 'Gen AI Team',
        created: new Date().toISOString(),
        global_config: {
          secret_manager_name: 'podium-ia-call-summarization-app-secrets',
          defaults: {
            region: 'us-east-1',
            language_model: 'anthropic.claude-3-5-sonnet-20240620-v1:0',
            storage_base: STORAGE_BASE,
            speech_to_text_model_name: 'azure_ai_speech',
            is_local_testing: 'False',
            max_output_token_length: 4096,
            temperature: 0.1,
            top_p: 0.9,
            top_k: 40,
          },
        },
        pipelines: [
          {
            name: PipelineName.CHAT_WITH_AUDIO,
            description:
              'Pipeline which takes documents and conversation history to get response from llm',
            enabled: 'True',
            inputs: {
              call_input: {
                type: 'record_id',
                value: !audioFile?.isS3Upload ? `${STORAGE_BASE}/${localStorage.getItem('userId')}/${audioFile?.id}/${audioFile?.file?.name}`: `s3://${audioFile?.currentPath}`,
              },
              file_id: !audioFile?.isS3Upload ? audioFile?.id: audioFile?.uuid,
            },
            modules: ['preprocess_audio', 'speech_to_text'],
          },
        ],
        modules: [
          {
            name: 'preprocess_audio',
            type: 'preprocess_audio_module',
            description: 'preprocess audio',
            inputs: {
              file_source: '${chat_with_audio_pipeline.inputs.call_input}',
            },
            outputs: {
              preprocessed_audio:
                '${global_config.defaults.storage_base}/${chat_with_audio_pipeline.inputs.file_id}/preprocessed_audio.mp3',
              audio_metadata:
                '${global_config.defaults.storage_base}/${chat_with_audio_pipeline.inputs.file_id}/audio_metadata.json',
            },
            depends_on: [],
          },
          {
            name: 'speech_to_text',
            type: 'speech_to_text_module',
            description: 'Convert call audio to text transcript with speaker ID.',
            model_name: 'azure_ai_speech',
            inputs: {
              file_source: '${preprocess_audio.outputs.preprocessed_audio}',
            },
            outputs: {
              transcription:
                '${global_config.defaults.storage_base}/${chat_with_audio_pipeline.inputs.file_id}/transcription.json',
              raw_transcription:
                '${global_config.defaults.storage_base}/${chat_with_audio_pipeline.inputs.file_id}/raw_transcription.txt',
            },
            depends_on: ['preprocess_audio'],
          },
        ],
      };
    case 'chat_with_transcript':
      return {
        version: '1.0',
        description: 'Config JSON for development',
        author: 'Gen AI Team',
        created: new Date().toISOString(),
        global_config: {
          secret_manager_name: 'podium-ia-call-summarization-app-secrets',
          defaults: {
            region: 'us-east-1',
            language_model: 'anthropic.claude-3-5-sonnet-20240620-v1:0',
            storage_base: STORAGE_BASE,
            speech_to_text_model_name: 'azure_ai_speech',
            is_local_testing: 'False',
            max_output_token_length: 4096,
            temperature: 0.1,
            top_p: 0.9,
            top_k: 40,
          },
        },
        pipelines: [
          {
            name: PipelineName.CHAT_WITH_AUDIO,
            description:
              'Pipeline which takes documents and conversation history to get response from llm',
            enabled: 'True',
            inputs: {
              call_input: {
                type: 'record_id',
                value: '',
              },
            },
            modules: ['llm_test_module'],
          },
        ],
        modules: [
          {
            name: 'llm_test_module',
            type: 'llm_chat_completion_module',
            description: 'LLM module for testing',
            llm_config: {
              provider_config: {
                model_name: modelConfig?.model_name,
              },
              max_output_token_length: modelConfig?.max_output_token_length,
              temperature: modelConfig?.temperature,
              top_p: modelConfig?.top_p,
              top_k: modelConfig?.top_k,
            },
            messages: [prompMessage, ...messages || []],
            outputs: {
              llm_response:
                '${global_config.defaults.storage_base}/${chat_with_audio_pipeline.inputs.run_id}/llm_response.txt',
            },
            depends_on: [],
          },
        ],
      };
    default:
      return {};
  }
};
